import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { pipelineService } from '../../../services/pipelineService';

const TemplateLibrary = ({ onApplyTemplate, projectId }) => {
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const templates = [
    {
      id: 'real-estate-standard',
      name: 'Real Estate Standard',
      description: 'Standard pipeline for real estate sales',
      stages: [
        { stageName: 'Nuevo Lead', color: '#F59E0B', icon: 'UserPlus', stageOrder: 1 },
        { stageName: 'Contactado', color: '#3B82F6', icon: 'Phone', stageOrder: 2 },
        { stageName: 'Calificado', color: '#8B5CF6', icon: 'CheckCircle', stageOrder: 3 },
        { stageName: 'Visita Agendada', color: '#EC4899', icon: 'Calendar', stageOrder: 4 },
        { stageName: 'Propuesta', color: '#F59E0B', icon: 'FileText', stageOrder: 5 },
        { stageName: 'Negociación', color: '#10B981', icon: 'MessageSquare', stageOrder: 6 },
        { stageName: 'Separación', color: '#06B6D4', icon: 'DollarSign', stageOrder: 7 },
        { stageName: 'Vendido', color: '#22C55E', icon: 'Trophy', stageOrder: 8 }
      ]
    },
    {
      id: 'quick-sale',
      name: 'Quick Sale Pipeline',
      description: 'Simplified pipeline for fast transactions',
      stages: [
        { stageName: 'Nuevo', color: '#3B82F6', icon: 'UserPlus', stageOrder: 1 },
        { stageName: 'Interesado', color: '#8B5CF6', icon: 'Star', stageOrder: 2 },
        { stageName: 'Propuesta', color: '#F59E0B', icon: 'FileText', stageOrder: 3 },
        { stageName: 'Cerrado', color: '#22C55E', icon: 'Trophy', stageOrder: 4 }
      ]
    },
    {
      id: 'luxury-property',
      name: 'Luxury Property',
      description: 'Extended pipeline for high-value properties',
      stages: [
        { stageName: 'Lead Inicial', color: '#F59E0B', icon: 'UserPlus', stageOrder: 1 },
        { stageName: 'Calificación Financiera', color: '#3B82F6', icon: 'DollarSign', stageOrder: 2 },
        { stageName: 'Primera Visita', color: '#8B5CF6', icon: 'Calendar', stageOrder: 3 },
        { stageName: 'Segunda Visita', color: '#EC4899', icon: 'Calendar', stageOrder: 4 },
        { stageName: 'Propuesta Formal', color: '#F59E0B', icon: 'FileText', stageOrder: 5 },
        { stageName: 'Negociación Avanzada', color: '#10B981', icon: 'MessageSquare', stageOrder: 6 },
        { stageName: 'Due Diligence', color: '#06B6D4', icon: 'CheckCircle', stageOrder: 7 },
        { stageName: 'Contrato', color: '#8B5CF6', icon: 'FileText', stageOrder: 8 },
        { stageName: 'Cierre', color: '#22C55E', icon: 'Trophy', stageOrder: 9 }
      ]
    }
  ];

  const handleApplyTemplate = async (template) => {
    try {
      // Create all stages from template
      for (const stage of template?.stages) {
        await pipelineService?.createPipelineStage({
          ...stage,
          projectId: projectId
        });
      }
      onApplyTemplate(projectId);
      setSelectedTemplate(null);
    } catch (error) {
      console.error('Error applying template:', error);
    }
  };

  return (
    <div className="bg-card rounded-xl p-4 sm:p-6 border border-border">
      <div className="flex items-center mb-6">
        <Icon name="Library" size={24} className="mr-3 text-primary" />
        <h2 className="text-xl font-semibold text-foreground">Pipeline Templates</h2>
      </div>

      <div className="space-y-4">
        {templates?.map((template) => (
          <div
            key={template?.id}
            className="p-4 bg-muted/20 rounded-lg border border-border hover:border-primary transition-all cursor-pointer"
            onClick={() => setSelectedTemplate(template)}
          >
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-1">{template?.name}</h3>
                <p className="text-sm text-muted-foreground">{template?.description}</p>
              </div>
              <div className="px-3 py-1 bg-primary/10 text-primary rounded-full text-xs font-medium">
                {template?.stages?.length} stages
              </div>
            </div>

            {/* Stage Preview */}
            <div className="flex flex-wrap gap-2">
              {template?.stages?.map((stage, index) => (
                <div
                  key={index}
                  className="flex items-center px-3 py-1.5 rounded-lg text-xs font-medium"
                  style={{ backgroundColor: `${stage?.color}20`, color: stage?.color }}
                >
                  <Icon name={stage?.icon} size={14} className="mr-1.5" />
                  {stage?.stageName}
                </div>
              ))}
            </div>

            <Button
              size="sm"
              iconName="Download"
              onClick={(e) => {
                e?.stopPropagation();
                handleApplyTemplate(template);
              }}
              className="mt-3 w-full"
            >
              Apply Template
            </Button>
          </div>
        ))}
      </div>

      {/* Template Detail Modal */}
      {selectedTemplate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
          <div className="bg-card rounded-xl p-6 max-w-2xl w-full border border-border max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-foreground">{selectedTemplate?.name}</h2>
              <button
                onClick={() => setSelectedTemplate(null)}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <Icon name="X" size={24} />
              </button>
            </div>

            <p className="text-muted-foreground mb-6">{selectedTemplate?.description}</p>

            <div className="space-y-3 mb-6">
              {selectedTemplate?.stages?.map((stage, index) => (
                <div
                  key={index}
                  className="flex items-center p-4 bg-muted/20 rounded-lg border border-border"
                >
                  <div className="flex items-center justify-center w-8 h-8 rounded-full bg-muted text-muted-foreground font-semibold text-sm mr-3">
                    {index + 1}
                  </div>
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center mr-3"
                    style={{ backgroundColor: `${stage?.color}20` }}
                  >
                    <Icon name={stage?.icon} size={20} color={stage?.color} />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-sm font-semibold text-foreground">{stage?.stageName}</h3>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                fullWidth
                onClick={() => setSelectedTemplate(null)}
              >
                Cancel
              </Button>
              <Button
                fullWidth
                iconName="Download"
                onClick={() => handleApplyTemplate(selectedTemplate)}
              >
                Apply Template
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TemplateLibrary;